import type { EntregaListItem, PendingEntregaPush } from '../domain/models/EntregaCatalog.ts';
import {
  entregaDeliveryMediaWarnings,
  entregaDeliveryReadinessIssues,
  formatEntregaDeliveryReadinessMessage,
  formatEntregaDeliveryWarningMessage,
  participanteDeliveryLabel,
  participanteRequiresDeliveryMedia,
  type EntregaDeliveryReadinessIssue,
  type EntregaDeliveryReadinessInput,
  type EntregaDeliveryReadinessParticipante,
} from '../domain/entregaDeliveryReadiness.ts';
import { blobHasSignatureInk } from '@src/modules/platform/signature/application/signatureBlobInk.ts';
import type { EntregaApplicationService } from './EntregaApplicationService.ts';

async function entregaLocalFotoLoaded(service: EntregaApplicationService, mediaId: string): Promise<boolean> {
  const id = mediaId.trim();
  if (!id) return false;
  const blob = await service.media.get(id);
  return Boolean(blob?.size);
}

async function entregaLocalFirmaLoaded(service: EntregaApplicationService, mediaId: string): Promise<boolean> {
  const id = mediaId.trim();
  if (!id) return false;
  const blob = await service.media.get(id);
  if (!blob?.size) return false;
  const ink = await blobHasSignatureInk(blob);
  return ink === true;
}

async function entregaLocalMediaLoaded(service: EntregaApplicationService, mediaId: string): Promise<boolean> {
  const id = mediaId.trim();
  if (!id) return false;
  return (await service.media.get(id)) != null;
}

/** Same rules as sync readiness — for participante list badges in entrega UI. */
export async function resolveParticipanteMediaCargadaFlags(
  service: EntregaApplicationService,
  participante: {
    entregaBolsa?: string | null;
    firmaMediaId?: string | null;
    fotoMediaId?: string | null;
  },
): Promise<{ firmaCargada: boolean; fotoCargada: boolean }> {
  if (!participanteRequiresDeliveryMedia(participante)) {
    return { firmaCargada: true, fotoCargada: true };
  }
  const [firmaCargada, fotoCargada] = await Promise.all([
    participante.firmaMediaId?.trim()
      ? entregaLocalFirmaLoaded(service, participante.firmaMediaId)
      : Promise.resolve(false),
    participante.fotoMediaId?.trim()
      ? entregaLocalFotoLoaded(service, participante.fotoMediaId)
      : Promise.resolve(false),
  ]);
  return { firmaCargada, fotoCargada };
}

function participanteReadinessFromEdit(
  participante: EntregaDeliveryReadinessParticipante,
): EntregaDeliveryReadinessParticipante {
  return {
    beneficiarioNombre: participante.beneficiarioNombre,
    beneficiarioDocumento: participante.beneficiarioDocumento,
    entregaBolsa: participante.entregaBolsa,
    firmaMediaId: participante.firmaMediaId,
    fotoMediaId: participante.fotoMediaId,
    acudienteAutorizado: participante.acudienteAutorizado,
    cedulaBeneficiarioFrontMediaId: participante.cedulaBeneficiarioFrontMediaId,
    cedulaBeneficiarioBackMediaId: participante.cedulaBeneficiarioBackMediaId,
    cedulaAutorizadoFrontMediaId: participante.cedulaAutorizadoFrontMediaId,
    cedulaAutorizadoBackMediaId: participante.cedulaAutorizadoBackMediaId,
    documentoAutorizacionMediaId: participante.documentoAutorizacionMediaId,
  };
}

async function appendMissingLocalMediaBlockers(
  service: EntregaApplicationService,
  input: EntregaDeliveryReadinessInput,
  issues: EntregaDeliveryReadinessIssue[],
): Promise<void> {
  for (const participante of input.participantes) {
    if (participante.acudienteAutorizado !== 'si') continue;
    const label = participanteDeliveryLabel(participante);
    const docChecks: Array<[string, string | null | undefined]> = [
      ['cédula del beneficiario (frontal)', participante.cedulaBeneficiarioFrontMediaId],
      ['cédula del beneficiario (trasera)', participante.cedulaBeneficiarioBackMediaId],
      ['cédula del autorizado (frontal)', participante.cedulaAutorizadoFrontMediaId],
      ['cédula del autorizado (trasera)', participante.cedulaAutorizadoBackMediaId],
      ['documento de autorización', participante.documentoAutorizacionMediaId],
    ];
    for (const [docLabel, mediaId] of docChecks) {
      if (mediaId?.trim() && !(await entregaLocalMediaLoaded(service, mediaId))) {
        issues.push({ message: `${label} sin ${docLabel} (archivo local faltante).` });
      }
    }
  }
}

async function appendMissingLocalMediaWarnings(
  service: EntregaApplicationService,
  input: EntregaDeliveryReadinessInput,
  issues: EntregaDeliveryReadinessIssue[],
): Promise<void> {
  for (const participante of input.participantes) {
    if (!participanteRequiresDeliveryMedia(participante)) continue;
    const label = participanteDeliveryLabel(participante);
    if (participante.firmaMediaId?.trim() && !(await entregaLocalFirmaLoaded(service, participante.firmaMediaId))) {
      issues.push({ message: `${label} sin firma (archivo local faltante).` });
    }
    if (participante.fotoMediaId?.trim() && !(await entregaLocalFotoLoaded(service, participante.fotoMediaId))) {
      issues.push({ message: `${label} sin foto (archivo local faltante).` });
    }
  }
  for (const [index, remision] of input.remisiones.entries()) {
    const prefix = input.remisiones.length > 1 ? `Remisión ${index + 1}` : 'Remisión';
    if (remision.firmaRecibeMediaId?.trim() && !(await entregaLocalFirmaLoaded(service, remision.firmaRecibeMediaId))) {
      issues.push({ message: `${prefix} sin firma de quien recibe (archivo local faltante).` });
    }
    if (remision.firmaEntregaMediaId?.trim() && !(await entregaLocalFirmaLoaded(service, remision.firmaEntregaMediaId))) {
      issues.push({ message: `${prefix} sin firma de quien entrega (archivo local faltante).` });
    }
  }
}

async function collectEntregaDeliveryReadinessIssues(
  service: EntregaApplicationService,
  input: EntregaDeliveryReadinessInput,
): Promise<EntregaDeliveryReadinessIssue[]> {
  const issues = entregaDeliveryReadinessIssues(input);
  await appendMissingLocalMediaBlockers(service, input, issues);
  return issues;
}

async function collectEntregaDeliveryMediaWarnings(
  service: EntregaApplicationService,
  input: EntregaDeliveryReadinessInput,
): Promise<EntregaDeliveryReadinessIssue[]> {
  const issues = entregaDeliveryMediaWarnings(input);
  await appendMissingLocalMediaWarnings(service, input, issues);
  return issues;
}

export function entregaDeliveryReadinessInputFromPending(row: PendingEntregaPush): EntregaDeliveryReadinessInput {
  return {
    participantes: row.participantes.map((participante) =>
      participanteReadinessFromEdit({
        beneficiarioNombre: participante.beneficiarioNombre,
        beneficiarioDocumento: participante.beneficiarioDocumento,
        entregaBolsa: participante.entregaBolsa,
        firmaMediaId: participante.firmaMediaId,
        fotoMediaId: participante.fotoMediaId,
        acudienteAutorizado: participante.acudienteAutorizado,
        cedulaBeneficiarioFrontMediaId: participante.cedulaBeneficiarioFrontMediaId,
        cedulaBeneficiarioBackMediaId: participante.cedulaBeneficiarioBackMediaId,
        cedulaAutorizadoFrontMediaId: participante.cedulaAutorizadoFrontMediaId,
        cedulaAutorizadoBackMediaId: participante.cedulaAutorizadoBackMediaId,
        documentoAutorizacionMediaId: participante.documentoAutorizacionMediaId,
      }),
    ),
    remisiones: row.remisiones.map((remision) => ({
      firmaRecibeMediaId: remision.firmaRecibeMediaId,
      firmaEntregaMediaId: remision.firmaEntregaMediaId,
    })),
  };
}

export async function loadEntregaDeliveryReadinessInput(
  service: EntregaApplicationService,
  entregaPublicId: string,
): Promise<EntregaDeliveryReadinessInput> {
  const [participantesList, remisiones] = await Promise.all([
    service.children.listParticipantesByEntrega(entregaPublicId),
    service.children.listRemisionesByEntrega(entregaPublicId),
  ]);
  const [participanteEdits, remisionEdits] = await Promise.all([
    Promise.all(participantesList.map((participante) => service.children.getParticipanteForEdit(participante.publicId))),
    Promise.all(remisiones.map((remision) => service.children.getRemisionForEdit(remision.publicId))),
  ]);
  return {
    participantes: participanteEdits.flatMap((participante) =>
      participante
        ? [
            participanteReadinessFromEdit({
              beneficiarioNombre: participante.beneficiarioNombre,
              beneficiarioDocumento: participante.beneficiarioDocumento,
              entregaBolsa: participante.entregaBolsa,
              firmaMediaId: participante.firmaMediaId,
              fotoMediaId: participante.fotoMediaId,
              acudienteAutorizado: participante.acudienteAutorizado,
              cedulaBeneficiarioFrontMediaId: participante.cedulaBeneficiarioFrontMediaId,
              cedulaBeneficiarioBackMediaId: participante.cedulaBeneficiarioBackMediaId,
              cedulaAutorizadoFrontMediaId: participante.cedulaAutorizadoFrontMediaId,
              cedulaAutorizadoBackMediaId: participante.cedulaAutorizadoBackMediaId,
              documentoAutorizacionMediaId: participante.documentoAutorizacionMediaId,
            }),
          ]
        : [],
    ),
    remisiones: remisionEdits.flatMap((remision) =>
      remision
        ? [
            {
              firmaRecibeMediaId: remision.firmaRecibeMediaId,
              firmaEntregaMediaId: remision.firmaEntregaMediaId,
            },
          ]
        : [],
    ),
  };
}

export async function checkEntregaDeliveryReady(
  service: EntregaApplicationService,
  opts: { publicId: string; puntoNombre?: string; action: 'pdf' | 'sync' },
): Promise<{ ok: true; warning?: string } | { ok: false; message: string }> {
  const input = await loadEntregaDeliveryReadinessInput(service, opts.publicId);
  const issues = await collectEntregaDeliveryReadinessIssues(service, input);
  if (issues.length > 0) {
    return {
      ok: false,
      message: formatEntregaDeliveryReadinessMessage(issues, {
        puntoNombre: opts.puntoNombre,
        action: opts.action,
      }),
    };
  }
  const warnings = await collectEntregaDeliveryMediaWarnings(service, input);
  const warning =
    warnings.length > 0
      ? formatEntregaDeliveryWarningMessage(warnings, { puntoNombre: opts.puntoNombre })
      : undefined;
  return warning ? { ok: true, warning } : { ok: true };
}

export async function checkEntregaDeliveryReadyFromPending(
  service: EntregaApplicationService,
  row: PendingEntregaPush,
  opts?: { puntoNombre?: string },
): Promise<{ ok: true; warning?: string } | { ok: false; message: string }> {
  const input = entregaDeliveryReadinessInputFromPending(row);
  const issues = await collectEntregaDeliveryReadinessIssues(service, input);
  if (issues.length > 0) {
    return {
      ok: false,
      message: formatEntregaDeliveryReadinessMessage(issues, {
        action: 'sync',
        puntoNombre: opts?.puntoNombre,
      }),
    };
  }
  const warnings = await collectEntregaDeliveryMediaWarnings(service, input);
  const warning =
    warnings.length > 0
      ? formatEntregaDeliveryWarningMessage(warnings, { puntoNombre: opts?.puntoNombre })
      : undefined;
  return warning ? { ok: true, warning } : { ok: true };
}

export async function checkEntregaListItemDeliveryReady(
  service: EntregaApplicationService,
  row: EntregaListItem,
  action: 'pdf' | 'sync',
): Promise<{ ok: true; warning?: string } | { ok: false; message: string }> {
  return checkEntregaDeliveryReady(service, {
    publicId: row.publicId,
    puntoNombre: row.puntoNombre,
    action,
  });
}
